import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, FileUploadZone } from '../common';
import ImageCropper from './ImageCropper';
import './BannerUpload.css';

/**
 * Banner upload section with drag-and-drop support.
 *
 * Displays either a placeholder with upload prompt or the uploaded
 * banner image with change/remove options.
 *
 * @param {Object} props
 * @param {string|null} props.bannerUrl - Current banner image URL
 * @param {function} props.onBannerChange - Handler for banner updates
 * @returns {JSX.Element}
 */
function BannerUpload({ bannerUrl, onBannerChange, onBannerFile, uploading = false }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState(null);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  const handleFileSelect = useCallback(
    (file) => {
      if (!file || !file.type.startsWith('image/')) return;
      const url = URL.createObjectURL(file);
      setTempImageFile(file);
      setTempImageUrl(url);
      setShowCropper(true);
      setIsModalOpen(true);
    },
    []
  );

  const resetBannerCropState = useCallback(() => {
    if (tempImageUrl) URL.revokeObjectURL(tempImageUrl);
    setTempImageFile(null);
    setTempImageUrl(null);
    setShowCropper(false);
  }, [tempImageUrl]);

  const handleCropComplete = useCallback(
    async (croppedFile) => {
      const fileToUpload = croppedFile || tempImageFile;
      if (!fileToUpload || !onBannerFile) return;

      try {
        await onBannerFile(fileToUpload);
      } catch (error) {
        console.error('Failed to upload banner image:', error);
      } finally {
        resetBannerCropState();
        setIsModalOpen(false);
      }
    },
    [onBannerFile, resetBannerCropState, tempImageFile]
  );

  const handleCropCancel = useCallback(() => {
    resetBannerCropState();
  }, [resetBannerCropState]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleModalClose = useCallback(() => {
    resetBannerCropState();
    setIsModalOpen(false);
  }, [resetBannerCropState]);

  return (
    <>
      <div
        className={`banner-upload ${bannerUrl ? 'banner-upload--has-image' : ''} ${isDragging ? 'banner-upload--dragging' : ''} ${uploading ? 'banner-upload--converting' : ''}`}
        onClick={() => !uploading && setIsModalOpen(true)}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && !uploading && setIsModalOpen(true)}
        aria-label={bannerUrl ? 'Change banner' : 'Upload banner'}
        aria-busy={uploading}
      >
        {uploading ? (
          <div className="banner-upload__placeholder">
            <span className="banner-upload__icon" aria-hidden="true">
              ⏳
            </span>
            <span className="banner-upload__text">
              Saving banner…
            </span>
          </div>
        ) : bannerUrl ? (
          <>
            <img
              src={bannerUrl}
              alt="Profile banner"
              className="banner-upload__image"
              referrerPolicy="no-referrer"
            />
            <div className="banner-upload__hover-overlay">
              <span>Change Banner</span>
            </div>
          </>
        ) : (
          <div className="banner-upload__placeholder">
            <span className="banner-upload__icon" aria-hidden="true">
              🖼
            </span>
            <span className="banner-upload__text">
              Drop your banner here or click to upload
            </span>
            <span className="banner-upload__subtext">
              Wide landscape images work best for your public profile.
            </span>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={showCropper ? 'Adjust Banner' : 'Upload Banner Photo'}
        className="banner-upload__modal"
      >
        {showCropper && tempImageUrl ? (
          <ImageCropper
            imageUrl={tempImageUrl}
            sourceFile={tempImageFile}
            onComplete={handleCropComplete}
            onCancel={handleCropCancel}
            instructions="Scroll to zoom, drag to reposition"
            cropWidth={520}
            cropHeight={130}
            outputWidth={1200}
            outputHeight={300}
            shape="banner"
          />
        ) : (
          <FileUploadZone
            onFileSelect={handleFileSelect}
            accept="image/*"
            prompt="Drop your banner here or click to upload"
            hint="Recommended size: 1200 x 300 pixels"
            icon={<span>🖼</span>}
          />
        )}
      </Modal>
    </>
  );
}

BannerUpload.propTypes = {
  bannerUrl: PropTypes.string,
  onBannerChange: PropTypes.func.isRequired,
  onBannerFile: PropTypes.func,
  uploading: PropTypes.bool,
};

export default BannerUpload;
